import React from 'react';
import Funnel from '@components/Widgets/Funnel';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import { flex } from '@fabric-ui/theme';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { getNumericValue } from '../../utils';
import { getIncidentStatus, getTotalAlerts, getTotalIncident } from '../queries';
import { FilterContext } from '../types';

interface AlertToIncidentsProps {
  filterContext: FilterContext;
}

const AlertToIncidents = (props: AlertToIncidentsProps) => {
  const theme = useTheme();
  const { filterContext } = props;

  const alertCount = useQueryObject({
    queryObject: getTotalAlerts(filterContext),
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data[0].value,
  });

  const incidentsCount = useQueryObject({
    queryObject: getTotalIncident(filterContext),
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data[0].value,
  });

  const incidentStatusCount = useQueryObject({
    queryObject: getIncidentStatus(filterContext),
    options: { placeholderData: keepPreviousData },
    onSuccess: data =>
      [
        {
          name: 'Investigated',
          value: getNumericValue(data.find(d => d['incident.current_status.name'] === 'Investigated')?.active_incidents_granular),
          color: theme.palette.colors.primary[500],
        },
        {
          name: 'Remediated',
          value: getNumericValue(data.find(d => d['incident.current_status.name'] === 'Remediated')?.active_incidents_granular),
          color: theme.palette.colors.primary[600],
        },
      ].map(item => ({ ...item, displayValues: [item.value, null] })),
  });

  const isLoading = alertCount.isLoading || incidentsCount.isLoading || incidentStatusCount.isLoading;
  const funnelValue: Array<Record<string, any>> = [];

  if (!isLoading && alertCount) {
    if (alertCount.data !== undefined && alertCount.data > 0)
      funnelValue.push({
        name: 'Alerts',
        value: alertCount.data,
        color: theme.palette.colors.primary[300],
        displayValues: [alertCount.data, null],
      });
    if (incidentsCount.data !== undefined && incidentsCount.data > 0)
      funnelValue.push({
        name: 'Incidents',
        value: incidentsCount.data,
        color: theme.palette.colors.primary[350],
        displayValues: [incidentsCount.data, null],
      });
    funnelValue.push(...(incidentStatusCount.data || []));
  }

  return (
    <Box sx={{ ...flex.col, padding: '16px 24px', gap: 1, minHeight: 250, height: '100%' }}>
      <Typography sx={{ whiteSpace: 'nowrap', fontSize: '18px' }} variant="h2">
        Alerts to Incidents
      </Typography>
      <Box sx={{ flex: 1, width: '100%' }}>
        {!isLoading ? (
          !funnelValue.some(v => v.value !== 0) ? (
            <Box sx={{ height: '100%', ...flex.row }}>
              <NoDataFoundWidget />
            </Box>
          ) : (
            <Funnel height="100%" barSize={16} labelsSize={12} data={funnelValue} />
          )
        ) : (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        )}
      </Box>
    </Box>
  );
};

export default AlertToIncidents;
